const API_URL = 'https://geo.api.gouv.fr';

const extractPostalCode = (value) => {
  const match = (value || '').match(/\b(\d{5})\b/);
  return match ? match[1] : '';
};

const normalizePostalPrefix = (value) => {
  const postalCode = extractPostalCode(value);
  if (postalCode) return postalCode;
  return (value || '').replace(/\D/g, '').slice(0, 5);
};

const formatPostalSuggestion = (city) => `${city.postalCode} - ${city.name}`;

const parseManualLocation = (value) => {
  const match = (value || '').match(/^\s*(\d{5})\s*[-–]\s*(.+?)\s*$/);
  if (!match) return null;
  return {
    postalCode: match[1],
    name: match[2],
  };
};

const uniqueCities = (items) => {
  const seen = new Set();
  return items
    .flatMap((item) => {
      const name = (item.nom || '').trim();
      const postalCodes = Array.isArray(item.codesPostaux) ? item.codesPostaux : [];
      return postalCodes.map((postalCode) => ({ name, postalCode }));
    })
    .filter((item) => item.name && item.postalCode)
    .filter((item) => {
      const key = `${item.postalCode}|${item.name}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => {
      if (left.postalCode !== right.postalCode) {
        return left.postalCode.localeCompare(right.postalCode);
      }
      return left.name.localeCompare(right.name, 'fr');
    });
};

const fetchCities = async ({ postalCode = '', city = '', limit = 10 }) => {
  const params = new URLSearchParams({
    fields: 'nom,codesPostaux',
    boost: 'population',
    limit: String(limit),
  });

  if (postalCode) {
    params.set('codePostal', postalCode);
  }

  if (city) {
    params.set('nom', city);
  }

  const response = await fetch(`${API_URL}/communes?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Unable to load French cities');
  }

  return uniqueCities(await response.json());
};

const getDepartmentCodes = (postalPrefix) => {
  if (postalPrefix.length < 2) return [];

  if (postalPrefix.startsWith('20')) {
    return ['2A', '2B'];
  }

  if ((postalPrefix.startsWith('97') || postalPrefix.startsWith('98')) && postalPrefix.length >= 3) {
    return [postalPrefix.slice(0, 3)];
  }

  return [postalPrefix.slice(0, 2)];
};

// A department's full commune list is fetched at most once per session and reused for
// every partial-prefix keystroke — so we keep exhaustive coverage (the largest French
// department has ~900 communes; limit 1000 returns them all, and the API only ever
// returns the communes that exist) without re-hitting the network as the user types.
const departmentCommunesCache = new Map();

const fetchDepartmentCommunes = (departmentCode) => {
  if (departmentCommunesCache.has(departmentCode)) {
    return departmentCommunesCache.get(departmentCode);
  }

  // Cache the in-flight promise (not just the resolved value) so rapid keystrokes
  // for the same department share one request instead of racing several. Evict on
  // failure so a later attempt can retry.
  const request = (async () => {
    const params = new URLSearchParams({
      fields: 'nom,codesPostaux',
      boost: 'population',
      limit: '1000',
    });

    const response = await fetch(`${API_URL}/departements/${departmentCode}/communes?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Unable to load French cities');
    }

    return response.json();
  })();

  departmentCommunesCache.set(departmentCode, request);
  request.catch(() => departmentCommunesCache.delete(departmentCode));
  return request;
};

const fetchCitiesByPostalPrefix = async (postalPrefix) => {
  if (postalPrefix.length === 5) {
    return fetchCities({ postalCode: postalPrefix, limit: 100 });
  }

  const departmentCodes = getDepartmentCodes(postalPrefix);
  if (departmentCodes.length === 0) return [];

  const payloads = await Promise.all(departmentCodes.map(fetchDepartmentCommunes));

  return uniqueCities(payloads.flat())
    .filter((city) => city.postalCode.startsWith(postalPrefix))
    .slice(0, 50);
};

const setCityDatalistOptions = (datalist, cities) => {
  if (!datalist) return;
  datalist.innerHTML = '';
  cities.forEach((city) => {
    const option = document.createElement('option');
    option.value = city.name;
    option.label = `${city.name} (${city.postalCode})`;
    option.dataset.postalCode = city.postalCode;
    datalist.appendChild(option);
  });
};

const ensureSuggestionList = (input, { marker, label }) => {
  const existing = input.parentElement?.querySelector(`[${marker}]`);
  if (existing) return existing;

  input.parentElement?.classList.add('mutualys-form-suggestions-wrap');
  const list = document.createElement('div');
  list.setAttribute(marker, '1');
  list.setAttribute('aria-label', label);
  list.className = 'mutualys-form-suggestions';
  list.style.display = 'none';
  input.insertAdjacentElement('afterend', list);
  return list;
};

const debounce = (callback, delay = 250) => {
  let timer = null;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => callback(...args), delay);
  };
};

export const getNormalizedPostalCodeValue = (value) => extractPostalCode(value) || normalizePostalPrefix(value);

export const setupFrenchCityAutocomplete = ({
  displayInput,
  postalInput,
  cityInput,
  cityDatalist,
  onPostalCodeSelected,
}) => {
  if (!postalInput || !cityInput) return;

  const searchInput = displayInput || postalInput;

  searchInput.removeAttribute('list');
  searchInput.setAttribute('autocomplete', 'off');

  let lastCities = [];
  let requestId = 0;
  let selectedCity = null;
  let manualFallbackEnabled = false;
  const postalSuggestionList = ensureSuggestionList(searchInput, {
    marker: 'data-postal-suggestion-list',
    label: 'Suggestions code postal et ville',
  });

  const applyCities = (cities) => {
    lastCities = cities;
    setCityDatalistOptions(cityDatalist, cities);
  };

  const hidePostalSuggestionList = () => {
    postalSuggestionList.style.display = 'none';
    postalSuggestionList.innerHTML = '';
  };

  const showPostalSuggestionLoading = () => {
    postalSuggestionList.innerHTML = '';
    const loading = document.createElement('div');
    loading.className = 'mutualys-form-suggestions-loading';
    loading.setAttribute('aria-live', 'polite');
    loading.innerHTML = '<span class="mutualys-form-suggestions-spinner" aria-hidden="true"></span><span>Recherche en cours…</span>';
    postalSuggestionList.appendChild(loading);
    postalSuggestionList.style.display = '';
  };

  const clearSelectedCity = () => {
    selectedCity = null;
    postalInput.value = '';
    cityInput.value = '';
    delete searchInput.dataset.selectedPostalCode;
    delete searchInput.dataset.selectedCity;
  };

  const enableManualLocationFallback = () => {
    if (manualFallbackEnabled) return;

    manualFallbackEnabled = true;
    const manualLocation = parseManualLocation(searchInput.value);
    postalInput.value = manualLocation?.postalCode || normalizePostalPrefix(searchInput.value);
    cityInput.value = manualLocation?.name || cityInput.value;

    hidePostalSuggestionList();
    searchInput.required = false;
    searchInput.style.display = 'none';
    searchInput.dataset.manualFallback = '1';

    [postalInput, cityInput].forEach((input) => {
      input.type = 'text';
      input.required = true;
      input.className = searchInput.className || 'form-control';
      input.style.marginTop = '8px';
    });

    postalInput.placeholder = postalInput.placeholder || 'Code postal';
    postalInput.inputMode = 'numeric';
    postalInput.setAttribute('autocomplete', 'postal-code');
    cityInput.placeholder = cityInput.placeholder || 'Ville';
    cityInput.setAttribute('autocomplete', 'address-level2');
  };

  const showPostalSuggestionList = (cities, postalPrefix) => {
    postalSuggestionList.innerHTML = '';

    if (cities.length === 0) {
      hidePostalSuggestionList();
      return;
    }

    const hint = document.createElement('div');
    hint.textContent = postalPrefix.length >= 5 ? 'Choisissez votre ville' : 'Suggestions';
    hint.className = 'mutualys-form-suggestions-title';
    postalSuggestionList.appendChild(hint);

    cities.forEach((city) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'mutualys-form-suggestion';
      option.textContent = formatPostalSuggestion(city);
      option.dataset.postalCode = city.postalCode;
      option.dataset.city = city.name;
      option.addEventListener('click', () => {
        applySelectedCity(city);
        hidePostalSuggestionList();
      });
      postalSuggestionList.appendChild(option);
    });

    postalSuggestionList.style.display = '';
  };

  const applySelectedCity = (selected, keepCombinedLabel = true) => {
    if (!selected) return;

    selectedCity = selected;
    cityInput.value = selected.name;
    postalInput.value = selected.postalCode;
    searchInput.value = keepCombinedLabel ? formatPostalSuggestion(selected) : selected.postalCode;
    searchInput.dataset.selectedPostalCode = selected.postalCode;
    searchInput.dataset.selectedCity = selected.name;
    searchInput.classList.remove('field-invalid', 'is-invalid');

    if (typeof onPostalCodeSelected === 'function') {
      onPostalCodeSelected(selected);
    }
  };

  const syncSelectionFromSearchInput = () => {
    const inputValue = searchInput.value.trim().toLowerCase();
    const selected = lastCities.find((city) => formatPostalSuggestion(city).toLowerCase() === inputValue);
    if (!selected) return false;

    applySelectedCity(selected);
    return true;
  };

  const searchByLocation = debounce(async () => {
    if (syncSelectionFromSearchInput()) {
      return;
    }

    const rawSearch = searchInput.value.trim();
    if (!rawSearch) {
      clearSelectedCity();
      applyCities([]);
      hidePostalSuggestionList();
      return;
    }

    if (!selectedCity || rawSearch !== formatPostalSuggestion(selectedCity)) {
      clearSelectedCity();
    }

    const postalPrefix = normalizePostalPrefix(rawSearch);
    if (postalPrefix !== rawSearch && /^\d{1,5}$/.test(rawSearch || '')) {
      searchInput.value = postalPrefix;
    }

    if (postalPrefix.length < 2 && rawSearch.length < 2) {
      applyCities([]);
      hidePostalSuggestionList();
      return;
    }

    const currentRequest = ++requestId;
    showPostalSuggestionLoading();
    try {
      const cities = postalPrefix.length >= 2
        ? await fetchCitiesByPostalPrefix(postalPrefix)
        : await fetchCities({ city: rawSearch, limit: 30 });
      if (currentRequest !== requestId) return;
      applyCities(cities);
      showPostalSuggestionList(cities, postalPrefix || rawSearch);

      const exactPostalMatches = cities.filter((city) => city.postalCode === postalPrefix);
      if (postalPrefix.length === 5 && exactPostalMatches.length === 1) {
        applySelectedCity(exactPostalMatches[0]);
        hidePostalSuggestionList();
      }
    } catch (err) {
      enableManualLocationFallback();
    }
  });

  searchInput.addEventListener('input', searchByLocation);
  searchInput.addEventListener('change', syncSelectionFromSearchInput);
  searchInput.addEventListener('blur', () => {
    syncSelectionFromSearchInput();
    window.setTimeout(() => {
      if (!postalSuggestionList.contains(document.activeElement)) {
        hidePostalSuggestionList();
      }
    }, 100);
  });
  searchInput.addEventListener('focus', () => {
    if (lastCities.length > 0) {
      showPostalSuggestionList(lastCities, normalizePostalPrefix(searchInput.value));
    }
  });

  const handleDocumentPointerDown = (event) => {
    if (event.target === searchInput || postalSuggestionList.contains(event.target)) {
      return;
    }

    hidePostalSuggestionList();
  };
  document.addEventListener('pointerdown', handleDocumentPointerDown);

  if (searchInput.value.trim()) {
    searchByLocation();
  }

  // Teardown handle. Callers that re-render their markup (e.g. the V2 multi-step
  // form) must call this before re-initializing, otherwise the document-level
  // pointerdown listener accumulates on every revisit to the step.
  return () => {
    document.removeEventListener('pointerdown', handleDocumentPointerDown);
    postalSuggestionList.remove();
  };
};
